/**
 * The one way a screen turns a failed API call into words for a person.
 *
 * FastAPI answers with `detail` as a sentence (our own errors) or, on a
 * request that failed validation, as a list of `{loc, msg}` objects. Screens
 * used to pass `detail` straight to a toast, so a validation error rendered
 * an object as a React child and took the whole app down.
 */
interface FieldError {
  loc?: unknown[]
  msg?: string
}

function fieldName(loc: unknown[] | undefined): string | null {
  // loc is e.g. ["body", "principal_email"]: the last string is the field.
  const last = [...(loc ?? [])].reverse().find((part) => typeof part === 'string' && part !== 'body')
  return typeof last === 'string' ? last.replace(/_/g, ' ') : null
}

function describe(item: FieldError): string | null {
  if (!item?.msg) return null
  // Pydantic prefixes custom validator messages with "Value error, ".
  const msg = item.msg.replace(/^Value error, /, '')
  const field = fieldName(item.loc)
  return field ? `${field}: ${msg}` : msg
}

/** The server's explanation of a failed call, or null when it gave none. */
export function apiErrorDetail(err: unknown): string | null {
  const response = (err as { response?: { data?: unknown } } | null)?.response
  const data = response?.data as { detail?: unknown; message?: unknown } | undefined
  const detail = data?.detail ?? data?.message
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail)) {
    const messages = detail.map((item) => describe(item as FieldError)).filter((m): m is string => Boolean(m))
    return messages.length ? messages.join('; ') : null
  }
  if (detail && typeof detail === 'object' && typeof (detail as { message?: unknown }).message === 'string') {
    return (detail as { message: string }).message
  }
  return null
}

/** Always a string, safe to render: the server's explanation or the fallback. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  return apiErrorDetail(err) ?? fallback
}
